import { Illustration, Text, type UseModalState } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { Box, Stack, ThemeProvider } from "@mui/material";
import { environmentConfig } from "@src/appV2/environment/config";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { FullScreenDialog } from "@src/appV2/lib/Dialogs/FullScreen/Dialog";
import { openInBrowser } from "@src/appV2/NativeAdapters/browser.adapter";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { PageHeaderTopRow } from "@src/appV2/redesign/components/PageHeaderTopRow";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { type ReactElement } from "react";

import { DialogAuthMethod, type DialogAuthMethodType } from "../types";

interface Props {
  modalState: UseModalState;
  authMethod: DialogAuthMethodType;
  userInput?: string;
  onClearForm?: () => void;
  onNavigateToSignUp?: () => void;
}

export function SignUpInsteadDialog(props: Props): ReactElement {
  const { modalState, authMethod, userInput, onClearForm, onNavigateToSignUp } = props;

  const theme = getTheme();

  useLogEffect(
    APP_V2_USER_EVENTS.SIGN_UP_INSTEAD_DIALOG_VIEWED,
    {
      authMethod,
      userInput: userInput ?? undefined,
    },
    { enabled: modalState.modalIsOpen }
  );

  return (
    <ThemeProvider theme={theme}>
      <FullScreenDialog modalState={modalState} variant="tertiary">
        <PageHeaderTopRow
          onClickBack={() => {
            modalState.closeModal();
            logEvent(APP_V2_USER_EVENTS.CLOSE_PHONE_NUMBER_EXISTS_DIALOG);
          }}
        />

        <Box
          sx={{
            flex: 1,
            padding: theme.spacing(6),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Stack
            spacing={8}
            sx={{
              textAlign: "center",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Illustration type="warning" />

            <Text variant="h2" sx={{ textAlign: "center" }}>
              {`This ${authMethod} isn't linked to a Clipboard account`}
            </Text>

            {userInput && (
              <Text variant="body1" sx={{ textAlign: "center" }}>
                You entered: {userInput}
              </Text>
            )}

            <Text
              variant="body1"
              sx={(theme) => ({
                textAlign: "center",
                color: theme.palette.text.secondary,
                maxWidth: "80%",
              })}
            >
              {authMethod === DialogAuthMethod.PHONE
                ? "Try a different phone number to log in or sign up with this one to create a new account"
                : "Try a different email to log in or sign up with a phone number to create an account"}
            </Text>
          </Stack>
        </Box>

        <DialogFooter orientation="vertical">
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={() => {
              onNavigateToSignUp?.();
            }}
          >
            Sign Up
          </Button>

          <Button
            fullWidth
            variant="outlined"
            size="large"
            onClick={() => {
              onClearForm?.();
            }}
          >
            Try a different {authMethod === DialogAuthMethod.PHONE ? "phone number" : authMethod}
          </Button>
        </DialogFooter>

        <Box sx={{ padding: theme.spacing(6), paddingTop: 2 }}>
          <Text
            variant="body2"
            sx={(theme) => ({
              textAlign: "center",
              color: theme.palette.text.secondary,
            })}
          >
            Trying to log in as a workplace user? Download the{" "}
            <TextButton
              underline
              sx={(theme) => ({
                color: theme.palette.text.secondary,
                display: "inline",
              })}
              onClick={async () => {
                await openInBrowser({ url: environmentConfig.REACT_APP_HCF_APP_URL });
              }}
            >
              Clipboard Workplace app
            </TextButton>
            .
          </Text>
        </Box>
      </FullScreenDialog>
    </ThemeProvider>
  );
}
